// Composer Studio — 속성 패널 숫자 입력 공통 훅 (실시간 미리보기 + Enter/blur 확정 + Esc 복원 + 화살표 증감)
//
// 편집 히스토리의 transient 2단계 모델(useEditorHistory: replacePresent/commitPresent/discardTransient)을
// 숫자 입력 UX에 배선한다. 표 열/행 경계 드래그(TableEdgeResizeOverlay)와 동일한 취지:
//   - 타이핑/스피너/화살표로 값이 변하는 동안 onPreview(replacePresent)로 캔버스에 즉시 반영(히스토리 미적재)
//   - Enter/blur 시 onCommit(commitPresent)으로 undo 1스텝 + isDirty
//   - Esc는 편집 시작 전 값으로 복원(onRevert=discardTransient) 후 blur
//   - ↑/↓ ±1, Shift ±10, Alt ±0.1(소수 1자리 반올림) — 증감도 실시간 미리보기, 확정은 Enter/blur
//
// 미커밋 미리보기가 blur 없이 이탈(다른 노드 선택 등으로 unmount)하는 경로는 revert로 통일한다.
// (일반 이탈은 클릭 전에 input blur가 먼저 발생해 정상 커밋되므로, unmount-without-blur는 드묾.)

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

/** 화살표 증감 스텝 3종(기본/Shift/Alt). 입력 성격별로 상향/하향 조정한다(lineHeight·투명도 등). */
export interface ArrowStepConfig {
  /** 수식어 없는 기본 스텝(기본 1). */
  base?: number;
  /** Shift 스텝(굵은 증감, 기본 10). */
  shift?: number;
  /** Alt 스텝(미세 증감, 기본 0.1). */
  alt?: number;
}

const DEFAULT_ARROW_STEP: Required<ArrowStepConfig> = { base: 1, shift: 10, alt: 0.1 };

/** 화살표 증감 스텝 결정: Alt > Shift > 기본(Alt 우선). cfg로 입력별 스텝 커스터마이즈. */
export function arrowStep(
  mods: { shiftKey: boolean; altKey: boolean },
  cfg?: ArrowStepConfig,
): number {
  const c = { ...DEFAULT_ARROW_STEP, ...cfg };
  if (mods.altKey) return c.alt;
  if (mods.shiftKey) return c.shift;
  return c.base;
}

/**
 * step 크기에 맞는 소수 자릿수로 반올림(부동소수 오차 제거).
 * step<1이면 자릿수만큼(0.1→1, 0.01→2, 0.05→2), step>=1이면 넉넉히(1e6) 반올림.
 */
function roundLikeStep(next: number, step: number): number {
  if (step >= 1) return Math.round(next * 1e6) / 1e6;
  const decimals = Math.max(0, Math.ceil(-Math.log10(step) - 1e-9));
  const f = Math.pow(10, decimals);
  return Math.round(next * f) / f;
}

/**
 * 현재값에 dir*step을 적용 + 부동소수 오차 정리 + min/max 클램프.
 * 소수 스텝(0.1·0.01 등)은 step 자릿수로 반올림(0.1+0.2 류 오차 제거), 그 외는 넉넉히 반올림.
 */
export function applyArrow(
  current: number,
  dir: 1 | -1,
  step: number,
  min?: number,
  max?: number,
): number {
  let next = current + dir * step;
  next = roundLikeStep(next, step);
  if (min != null && next < min) next = min;
  if (max != null && next > max) next = max;
  // -0 정규화(Math.round(-0.5) 등이 만든 -0 → +0). 표시/비교 일관성.
  if (next === 0) next = 0;
  return next;
}

/**
 * 편집 히스토리 transient 2단계(replacePresent/commitPresent/discardTransient)를 배선하는 핸들러 3종의
 * 공용 이름 계약. 속성 패널/셀 편집기의 패치 핸들러 트리오를 이 이름(onPreview/onCommit/onRevert)으로
 * 통일한다(과거 onPatchLive/onLivePatch/onPreviewPatch·onPatchCommit/onCommitPatch·onRevertPatch/onDiscardDim
 * 혼재를 수렴). T = 적용 패치 타입(Partial<ComposerNode> / Partial<TableCell> 등).
 */
export interface PreviewPatchHandlers<T> {
  /** 실시간 미리보기(replacePresent) — 값이 바뀌는 동안 캔버스에 즉시 반영(히스토리 미적재). */
  onPreview: (patch: T) => void;
  /** 확정(commitPresent) — Enter/blur/pointerup 시 undo 1스텝 적재. */
  onCommit: (patch: T) => void;
  /** 미리보기 폐기(discardTransient) — Esc/무변경/이탈 시 편집 시작 전 값으로 복원. */
  onRevert: () => void;
}

/** 입력 요소에 스프레드할 props(값 + 이벤트 핸들러). */
export interface NumericPreviewInputProps {
  value: string;
  onFocus: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

export interface UseNumericPreviewInputOptions {
  /** 외부 확정값의 표시 문자열(비편집 시 동기화 기준). */
  display: string;
  /** 표시 문자열 → 숫자(화살표 증감 기준). 숫자가 아니면 null. */
  parse: (raw: string) => number | null;
  /** 숫자 → 표시 문자열(화살표 증감 결과 반영). */
  format: (n: number) => string;
  /** 유효 입력 실시간 미리보기(replacePresent). canPreview 통과 시에만 호출. */
  onPreview: (raw: string) => void;
  /** 확정(commitPresent). Enter/blur. 값 무변경이면 호출되지 않음(대신 onRevert). */
  onCommit: (raw: string) => void;
  /** 미리보기 폐기 — 편집 시작 전 값으로 복원(discardTransient). Esc/무변경/이탈. */
  onRevert: () => void;
  /** 미리보기 가능 판정. 기본: parse(raw)!==null(빈칸/'-' 등은 skip). */
  canPreview?: (raw: string) => boolean;
  min?: number;
  max?: number;
  /** 화살표 증감 스텝 커스터마이즈(입력 성격별 — 예: lineHeight base 0.1/alt 0.01). 미지정=1/10/0.1. */
  stepConfig?: ArrowStepConfig;
}

/**
 * 숫자 입력 공통 UX 훅. 반환 props를 `<input {...props} />`로 스프레드한다.
 * type/min/step/placeholder/className 등은 각 컴포넌트가 별도로 지정(키 충돌 없음).
 */
export function useNumericPreviewInput(
  opts: UseNumericPreviewInputOptions,
): NumericPreviewInputProps {
  const [local, setLocal] = useState(opts.display);

  // 편집 중 여부 — 미리보기로 바뀐 display가 입력을 덮어쓰지 않게 동기화를 가드한다.
  const editingRef = useRef(false);
  // 편집 시작 시점의 확정 표시값(Esc 복원 + 무변경 판정 기준).
  const startRef = useRef(opts.display);
  // 최신 local/opts 참조(unmount 클린업 + 이벤트 핸들러가 stale closure 없이 사용).
  const localRef = useRef(local);
  localRef.current = local;
  const latest = useRef(opts);
  latest.current = opts;

  // 외부 확정값 동기화 — 편집 중이 아닐 때만.
  useEffect(() => {
    if (!editingRef.current) setLocal(opts.display);
  }, [opts.display]);

  const previewIfValid = useCallback((raw: string) => {
    const o = latest.current;
    const ok = o.canPreview ? o.canPreview(raw) : o.parse(raw) !== null;
    if (ok) o.onPreview(raw);
  }, []);

  const onFocus = useCallback(() => {
    editingRef.current = true;
    startRef.current = latest.current.display;
  }, []);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      editingRef.current = true;
      const raw = e.target.value;
      setLocal(raw);
      previewIfValid(raw);
    },
    [previewIfValid],
  );

  const onBlur = useCallback(() => {
    if (!editingRef.current) return; // Esc 후 발생하는 blur 중복 방지
    editingRef.current = false;
    const raw = localRef.current;
    if (raw === startRef.current) latest.current.onRevert();
    else latest.current.onCommit(raw);
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const o = latest.current;
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur(); // onBlur가 커밋
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        editingRef.current = false;
        setLocal(startRef.current);
        o.onRevert(); // 미리보기 폐기 → 원값 복원
        (e.target as HTMLInputElement).blur();
        return;
      }
      // Ctrl/Cmd+Z·Y는 여기서 처리하지 않는다 — 컨테이너(EditorPage 단축키)가 "blur로 편집 확정
      // 후 앱 undo/redo"를 수행한다(blur가 이 훅의 onBlur 커밋을 태워 draft가 정식 1스텝이 되고,
      // undo 후 Shift+Ctrl+Z 재실행이 가능). 편집 '취소'는 Esc 전용(폐기 — redo 없음).
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const cur = o.parse(localRef.current);
        if (cur == null) return; // 숫자 아니면 증감 안 함(네이티브 동작도 그대로)
        e.preventDefault(); // 네이티브 number 스피너(±step) 억제 후 커스텀 스텝 적용
        editingRef.current = true;
        const dir = e.key === 'ArrowUp' ? 1 : -1;
        const nextStr = o.format(applyArrow(cur, dir, arrowStep(e, o.stepConfig), o.min, o.max));
        setLocal(nextStr);
        previewIfValid(nextStr);
      }
    },
    [previewIfValid],
  );

  // unmount 시 미커밋 미리보기 처리(blur 없이 이탈하는 경로) — revert로 통일.
  useEffect(
    () => () => {
      if (!editingRef.current) return;
      editingRef.current = false;
      latest.current.onRevert();
    },
    [],
  );

  return { value: local, onFocus, onChange, onKeyDown, onBlur };
}
